import $ from 'jquery';

import AlerterView from 'chairisher/view/alerter';

import { isUnauthorized } from 'chairisher/util/status';
import { getQuickCreateUrl } from 'chairisher/context/auth';
import { getProductsJsonToTrack } from 'chairisher/context/search';

const MAX_PRODUCTS_TO_TRACK = 4;

class SavedSearchForm {
    /**
     * Events fired by the SavedSearchForm.
     * @readonly
     * @static
     * @type {{TOGGLE: string}}
     */
    static Event = {
        TOGGLE: 'savedsearchform:toggle', // indicates the user has toggled followed or unfollowed a search a search
    };

    /**
     * @param {Object=} options
     * @param {string=} options.formSelector Selector of the saved search form. Defaults to '#js-form-saved-search'
     * @param {string=} options.buttonContainerSelector Selector of the buttons to hide or show. Defaults to '.buttonSelectorFollow, .buttonSelectorUnfollow'
     * @param {string=} options.buttonSelectorFollow Selector of the "Follow" button
     * @param {string=} options.buttonSelectorUnfollow Selector of the "Unfollow" button
     * @constructor
     */
    constructor({
        formSelector = '.js-form-saved-search',
        buttonContainerSelector = '',
        buttonSelectorFollow = '.js-btn-save-search',
        buttonSelectorUnfollow = '.js-btn-remove-search',
    } = {}) {
        this.formSelector = formSelector;
        this.buttonSelectorFollow = buttonSelectorFollow;
        this.buttonSelectorUnfollow = buttonSelectorUnfollow;
        this.buttonContainerSelector =
            buttonContainerSelector || [this.buttonSelectorFollow, this.buttonSelectorUnfollow].join(', ');
    }

    /**
     * Follows a search
     * @returns {$.Deferred}
     */
    follow() {
        const $form = $(this.formSelector);

        return this.toggleFollow({
            data: $form.serialize(),
            type: $form.attr('method'),
            url: $form.attr('action'),
        }).done((data) => {
            $(this.buttonSelectorUnfollow).attr('href', data.delete_url);
        });
    }

    /**
     * Unfollows a search
     * @returns {$.Deferred}
     */
    unfollow() {
        const $unfollowButton = $(this.buttonSelectorUnfollow);
        return this.toggleFollow({
            url: $unfollowButton.attr('href') || $unfollowButton.data('href'),
        });
    }

    /**
     * Toggles a search to be either followed or unfollowed, and ensures buttons are disabled, toggled, and reenabled
     * at the appropriate time.
     *
     * @param {Object} ajaxSettings
     * @param {Object=} ajaxSettings.data Optional object containing data to submit
     * @param {string} ajaxSettings.url URL endpoint to hit
     * @param {string=} ajaxSettings.type Optional HTTP method type. Defaults to 'GET'
     * @returns {$.Deferred}
     */
    toggleFollow(ajaxSettings) {
        const $buttonContainer = $(this.buttonContainerSelector);
        $buttonContainer.popover('hide');

        return $.ajax({
            authUrl: this.generateAuthUrl(),
            ...ajaxSettings,
        })
            .done((data) => {
                $buttonContainer.toggleClass('hidden');
                AlerterView.success($('<span></span>', { html: data.messages[0].message }));
                $(this.formSelector).trigger(SavedSearchForm.Event.TOGGLE);
            })
            .fail((jqXHR) => {
                if (!isUnauthorized(jqXHR?.status)) {
                    // todo: i18n
                    AlerterView.error(
                        'An error occurred while trying to remove your saved search. Please try again later.',
                    );
                }
            });
    }

    /**
     * @returns {string} URL to use in AJAX requests in case the user is not authenticated
     */
    generateAuthUrl() {
        const products = [];
        const contextProducts = getProductsJsonToTrack();
        const variant = $(this.buttonSelectorFollow).data('is-seller-follow') ? 'seller-follow' : 'saved-search';

        if (contextProducts && contextProducts.length >= MAX_PRODUCTS_TO_TRACK) {
            for (let i = 0; i < MAX_PRODUCTS_TO_TRACK; i += 1) {
                products.push(contextProducts[i].id);
            }
        }

        return `${getQuickCreateUrl(variant)}&product-ids=${encodeURIComponent(JSON.stringify(products))}`;
    }
}

export default SavedSearchForm;
